import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { PageHero } from "@/components/PublicLayout";
import { TextField, TextAreaField, SelectField } from "@/components/FormKit";
import { COMPANY } from "@/lib/data";

const empty = {
  first_name: "", last_name: "", phone: "", email: "",
  pickup_address: "", destination_address: "", transportation_date: "",
  requested_pickup_time: "", appointment_time: "", transportation_type: "",
  trip_type: "", passengers: "1", additional_info: "",
};

export default function BookRide() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => set(k)(e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone || !form.pickup_address ||
        !form.destination_address || !form.transportation_date || !form.transportation_type || !form.trip_type) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/ride-requests", form);
      setDone(true);
      toast.success("Ride request submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <SuccessScreen title="Ride Request Received" />;

  return (
    <>
      <PageHero eyebrow="Book a Ride" title="Request Your Transportation"
        subtitle="Complete the form below and a CareDeserved representative will contact you to confirm your trip." />
      <section className="bg-brand-bg py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} data-testid="book-ride-form" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField label="First Name" required testid="ride-first" value={form.first_name} onChange={setE("first_name")} />
              <TextField label="Last Name" required testid="ride-last" value={form.last_name} onChange={setE("last_name")} />
              <TextField label="Phone Number" required testid="ride-phone" type="tel" value={form.phone} onChange={setE("phone")} />
              <TextField label="Email" testid="ride-email" type="email" value={form.email} onChange={setE("email")} />
              <TextField label="Pickup Address" required testid="ride-pickup" wrapClass="sm:col-span-2" value={form.pickup_address} onChange={setE("pickup_address")} />
              <TextField label="Destination Address" required testid="ride-destination" wrapClass="sm:col-span-2" value={form.destination_address} onChange={setE("destination_address")} />
              <TextField label="Transportation Date" required testid="ride-date" type="date" value={form.transportation_date} onChange={setE("transportation_date")} />
              <TextField label="Requested Pickup Time" testid="ride-pickup-time" type="time" value={form.requested_pickup_time} onChange={setE("requested_pickup_time")} />
              <TextField label="Appointment Time" testid="ride-appt-time" type="time" value={form.appointment_time} onChange={setE("appointment_time")} />
              <TextField label="Number of Passengers" testid="ride-passengers" type="number" min="1" value={form.passengers} onChange={setE("passengers")} />
              <SelectField label="Transportation Type" required testid="ride-type" value={form.transportation_type}
                onValueChange={set("transportation_type")} placeholder="Select type" options={["Ambulatory", "Wheelchair", "Other"]} />
              <SelectField label="One Way or Round Trip" required testid="ride-trip" value={form.trip_type}
                onValueChange={set("trip_type")} placeholder="Select trip" options={["One Way", "Round Trip"]} />
              <TextAreaField label="Additional Information" testid="ride-info" wrapClass="sm:col-span-2"
                value={form.additional_info} onChange={setE("additional_info")} placeholder="Mobility needs, escort, special instructions…" />
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
              <Icon name="ShieldCheck" size={20} weight="fill" className="mt-0.5 shrink-0" />
              Submitting a ride request does not guarantee transportation. A CareDeserved representative will contact you to confirm availability.
            </div>

            <Button type="submit" disabled={loading} data-testid="ride-submit"
              className="mt-6 h-14 w-full rounded-full bg-brand-purple text-base font-semibold text-white hover:bg-brand-purple2">
              {loading ? "Submitting…" : "Submit Ride Request"}
            </Button>
            <p className="mt-4 text-center text-sm text-slate-500">
              Prefer to call? <a href={COMPANY.phoneHref} className="font-semibold text-brand-blue">{COMPANY.phone}</a>
            </p>
          </form>
        </div>
      </section>
    </>
  );
}

export function SuccessScreen({ title, message }) {
  return (
    <section className="flex min-h-[70vh] items-center justify-center bg-brand-bg px-4 py-20">
      <div className="max-w-md rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-teal/10 text-brand-teal">
          <Icon name="CheckCircle" size={38} weight="fill" />
        </span>
        <h2 className="mt-5 font-heading text-2xl font-bold text-slate-900">{title}</h2>
        <p className="mt-3 text-slate-600">
          {message || "Thank you! A CareDeserved representative will contact you shortly to confirm details. If your request is time-sensitive, please call us."}
        </p>
        <a href={COMPANY.phoneHref} className="mt-6 inline-flex items-center gap-2 font-heading text-xl font-bold text-brand-blue">
          <Icon name="Phone" size={22} weight="fill" /> {COMPANY.phone}
        </a>
      </div>
    </section>
  );
}
